import { defineComponent, defineAsyncComponent, ref, Ref, onMounted, onUnmounted, watch, computed } from 'vue';
import context, { Constants, ITaskCreator, ISmsService, TextConversationModel } from '@truckdown/systems';
import { ServiceRequestStatusChecker } from '@truckdown/servicetransactions/ServiceRequestStatusChecker';
import { UserStatusResult } from '@truckdown/servicetransactions/UserStatusResult';
import { Constants as LocalConstants } from '../../constants';
import * as signalR from '@microsoft/signalr';
import error from "../Error/Error.vue";
import loading from "../Loading/Loading.vue";

interface IProps {

}

interface IData {
    isAuthenticated: Ref<boolean>;
    isCsr: Ref<boolean>;
    firstName: Ref<string | undefined>;
    lastName: Ref<string | undefined>;
    profilePicture: Ref<string | undefined>;

    showControlPanel: Ref<boolean>,
    adminSearchSelected: Ref<boolean>,
    adminSearchString: Ref<string>

    unassignedTaskCount: Ref<number>;
    taskCount: Ref<number>;

    unreadConversations: Ref<number>;
    conversationCount: Ref<number>;

    showFleetSetupWidget: Ref<boolean>;
    showMenu: Ref<boolean>;

    requestStatus: Ref<UserStatusResult>;
    requestToOpen: Ref<string | undefined>;
}

const getData = function (props: IProps) {
    return {
        isAuthenticated: ref<boolean>(false),
        isCsr: ref<boolean>(false),
        firstName: ref<string | undefined>(),
        lastName: ref<string | undefined>(),
        profilePicture: ref<string | undefined>(),

        showControlPanel: ref<boolean>(false),
        adminSearchSelected: ref<boolean>(false),
        adminSearchString: ref<string>(''),

        unassignedTaskCount: ref<number>(0),
        taskCount: ref<number>(0),

        unreadConversations: ref<number>(0),
        conversationCount: ref<number>(0),

        showFleetSetupWidget: ref<boolean>(false),
        showMenu: ref<boolean>(false),

        requestStatus: ref<UserStatusResult>({
            vendor: {
                isActive: false,
                new: 0,
                active: 0
            },
            fleet: {
                isActive: false,
                new: 0,
                active: 0
            }
        }),
        requestToOpen: ref<string | undefined>()
    } as IData;
}

const setData = function (data: IData) {
    data.isAuthenticated.value = context.isAuthenticated;
    data.isCsr.value = context.isInRole(Constants.Roles.SystemRole)
        || context.isInRole(Constants.Roles.CSRRole);

    data.firstName.value = context.user?.firstName;
    data.lastName.value = context.user?.lastName;

    const profileSetting = context.settings['profilepic'];
    const apiUrl = context.apis['api'].url;
    
    if (profileSetting) {
        data.profilePicture.value = apiUrl + '/systems/profile-image/' + encodeURIComponent(profileSetting);
    } else {
        data.profilePicture.value = apiUrl + '/systems/profile-image';
    }

    data.showFleetSetupWidget.value = fleetSetupWidgetCheck();
}

const fleetSetupWidgetCheck = function (): boolean {
    if (!context.isAuthenticated) {
        return false;
    }

    if (context.isInRole("General Admin")
        && !context.isInRole("Fleet User")
        && context.settings["accounttype"] != "Vendor"
        && context.settings["fleet_onboarding"] != "complete"
        && context.settings["fleet_onboarding"] != "dismissed") {
        return true;
    }

    return false;
}

const getComputed = function (data: IData) {
    return {
        conversationTitle: computed(() => {
            const conversations = data.conversationCount.value + ' conversation' + (data.conversationCount.value == 1 ? '' : 's');
            const unread = data.unreadConversations.value + ' unread';
            return conversations + ', ' + unread;
        })
    }
}

const getMethods = function (data: IData) {
    return {
        toggleMenu: function (evt: Event) {
            data.showMenu.value = !data.showMenu.value;
            if (data.showMenu.value) {
                context.events.emit(LocalConstants.Events.OpenMenu, 'main-menu');
            }
        },
        closeMenu: function (evt: Event) {
            data.showMenu.value = false;
        },
        toggleControlPanel: function (evt: Event) {
            data.showControlPanel.value = !data.showControlPanel.value;
            if (data.showControlPanel.value) {
                context.events.emit(LocalConstants.Events.OpenMenu, 'control-panel');
            }
        },

        closeControlPanel: function (evt: Event) {
            data.showControlPanel.value = false;
        },

        adminSearchFocusInput: function (evt: Event) {
            data.adminSearchSelected.value = true;
        },

        adminSearchBlurInput: function (evt: Event) {
            data.adminSearchSelected.value = false;
        },

        adminSearchInputKeyUp: function (evt: KeyboardEvent) {
            if (!data.showControlPanel.value) {
                data.showControlPanel.value = true; 
                context.events.emit(LocalConstants.Events.OpenMenu, 'control-panel');
                if (evt.target && (evt.target as any).blur) {
                    (evt.target as any).blur();
                }
            }
        }
    };
}

const assignTaskCounts = async function (data: IData) {
    try {
        if (data.isCsr.value) {
            const service = await context.getService<ITaskCreator>('ITaskCreator');
            const res = await service.getCounts();
            if (res.ok) {
                data.taskCount.value = res.data ?? 0;
                data.unassignedTaskCount.value = res.data2 ?? 0;
            } else {
                data.unassignedTaskCount.value = 0;
                data.taskCount.value = 0;
            }
        } else {
            data.unassignedTaskCount.value = 0;
            data.taskCount.value = 0;
        }

    }
    catch (err) {
        console.log(err);
        data.unassignedTaskCount.value = 0;
        data.taskCount.value = 0;
    }
}

const setConversationCounts = async function (data: IData) {
    try {
        if (data.isCsr.value) {
            let service = await context.getService<ISmsService>('ISmsService');
            let res = await service.conversationCount();
            if (res.ok) {
                data.conversationCount.value = res.data ?? 0;
                data.unreadConversations.value = res.data2 ?? 0;
            } else {
                console.log('Error getting conversation counts');
            }
        }
    }
    catch (err) {
        console.log(err);
    }
}

const initializeWebsocket = function (data: IData) {
    if (data.isCsr.value) {
        let connection = new signalR.HubConnectionBuilder()
            .withUrl('/systems/csr-hub')
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
                    if (retryContext.elapsedMilliseconds < 60000) { // Retry for 1 minute
                        return 5000; //try every 5 seconds for 1 min
                    } else if (retryContext.elapsedMilliseconds < 900000) {
                        return 10000; //try every 10 seconds for 15 minutes
                    } else {
                        return 60000; //try every minute
                    }
                }
            })
            .build();

        connection.onreconnecting((error: any) => {
            console.warn("Connection lost. Reconnecting...", error);
        });

        connection.onreconnected(async () => {
            console.log("Connection reestablished.");
            await setConversationCounts(data);
        });

        connection.onclose((error: any) => {
            console.error("Connection closed.", error);
        });

        connection.on('updateSupportConversation', async (val: TextConversationModel) => {
            try {
                context.events.emit('updateSupportConversation', val);
            }
            catch (err) {
                console.log('Error receiving updateSupportConversation message', err, val);
            }
        });

        connection.on('new-task', async (val: any) => {
            try {
                context.events.emit('new-task', val);
            }
            catch (err) {
                console.log('Error receiving newTask message', err, val);
            }
        });

        connection.on('update-task', async (val: any) => {
            try {
                context.events.emit('update-task', val);
            }
            catch (err) {
                console.log('Error receiving updateTask message', err, val);
            }
        });

        connection.on('update-task-counts', async (val: any) => {
            try {
                context.events.emit('update-task-counts', val);
            }
            catch (err) {
                console.log('Error receiving updateTaskCounts message', err, val);
            }
        });

        connection.start().catch((err: any) => console.log('Websocket connection error', err));
        return connection;
    }
}

const loadServiceRequestStatus = async function (data: IData) {
    try {
        let service = new ServiceRequestStatusChecker(context);
        data.requestStatus.value = await service.getStatus();
    }
    catch (err) {
        console.log(err);
        data.requestStatus.value = {
            vendor: {
                isActive: false,
                new: 0,
                active: 0
            },
            fleet: {
                isActive: false,
                new: 0,
                active: 0
            }
        };
    }
}

export default defineComponent({
    name: 'header-icons',
    props: {
    },
    components: {
        'header-menu-list': defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-header-menu-list" */ './HeaderMenuList/HeaderMenuList.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 3000, //default 3000 - Infinity
            suspensible: false
        }),
        'control-panel': defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-control-panel" */ './ControlPanel/ControlPanel.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 3000, //default 3000 - Infinity
            suspensible: false
        }),
        //note: requests-link uses resolveComponent which runs when the component is rendered early in the pipeline. Moving this to an async component solved the timing issues.
        'requests-link': defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-request-link" */ './RequestsLink/RequestsLink.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 3000, //default 3000 - Infinity
            suspensible: false
        })
    },
    setup: function (props: IProps) {
        const data = getData(props);
        setData(data);

        const computed = getComputed(data);
        const methods = getMethods(data);

        const handleOpenMenu = function (menu: string) {
            if (menu === 'main-menu') {
                data.showControlPanel.value = false;
            } else if (menu === 'control-panel') {
                data.showMenu.value = false;
            } else {
                data.showControlPanel.value = false;
                data.showMenu.value = false;
            }
        }

        const refreshContext = async function () {
            setData(data);
            if (context.isAuthenticated) {
                await loadServiceRequestStatus(data);
            }
        }
        const handleSRRefresh = async function (requestId?: string, openRequest?: boolean) {
            console.log('SR Refresh event received. RequestId: ' + requestId + ', openRequest: ' + openRequest);
            await loadServiceRequestStatus(data);
            if (openRequest && requestId && requestId.length > 0) {
                data.requestToOpen.value = requestId;
            }
        }
        const handleUpdateTask = async function () {
            await assignTaskCounts(data);
        }
        const handleUpdateConversations = async function () {
            await setConversationCounts(data);
        }

        onMounted(async () => {
            context.events.on(LocalConstants.Events.OpenMenu, handleOpenMenu);
            context.events.on(LocalConstants.Events.RefreshOnboardingProgress, refreshContext);
            context.events.on(LocalConstants.Events.RefreshRequestsLink, handleSRRefresh);

            if (context.isAuthenticated) {
                await loadServiceRequestStatus(data);
            }

            if (data.isCsr.value) {
                await assignTaskCounts(data);
                await setConversationCounts(data);
                initializeWebsocket(data);

                context.events.on('new-task', handleUpdateTask);
                context.events.on('update-task', handleUpdateTask);
                context.events.on('update-task-counts', handleUpdateTask);
                context.events.on('updateSupportConversation', handleUpdateConversations);
            }
        });

        onUnmounted(() => {
            context.events.off(LocalConstants.Events.OpenMenu, handleOpenMenu);
            context.events.off(LocalConstants.Events.RefreshOnboardingProgress, refreshContext);
            context.events.off(LocalConstants.Events.RefreshRequestsLink, handleSRRefresh);

            if (data.isCsr.value) {
                context.events.off('new-task', handleUpdateTask);
                context.events.off('update-task', handleUpdateTask);
                context.events.off('update-task-counts', handleUpdateTask);
                context.events.off('updateSupportConversation', handleUpdateConversations);
            }
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});